
import { useState, useEffect } from 'react';
import { DerivativeCreation } from '@/data/models/types';
import { derivativesApi } from '@/api/derivatives';
import { fetchPlaylistById } from '@/utils/supabase/playlistFetching';

export const usePlaylistDerivatives = (playlistId?: string, limit: number = 1000) => {
  const [derivatives, setDerivatives] = useState<DerivativeCreation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [usedMockData, setUsedMockData] = useState(false);
  const [actualPlaylistId, setActualPlaylistId] = useState<string | null>(null);

  // First, resolve the playlistId if it's a slug/code instead of a UUID
  useEffect(() => {
    const resolvePlaylistId = async () => {
      if (!playlistId) {
        setIsLoading(false);
        return;
      }
      
      // Check if we have a UUID or a slug/code
      if (playlistId.startsWith('playlist-')) {
        try {
          console.log(`Resolving playlist code: ${playlistId}`);
          
          // Mock data mapping for development
          const mockPlaylistMap: Record<string, string> = {
            'playlist-5': '6eca71d9-371e-4223-8e96-478d9a38a0a8'
          };
          
          if (mockPlaylistMap[playlistId]) {
            console.log(`Found mock playlist ID: ${mockPlaylistMap[playlistId]}`);
            setActualPlaylistId(mockPlaylistMap[playlistId]);
          } else {
            // Fetch from database for real deployment
            const playlist = await fetchPlaylistById(playlistId);
            if (playlist?.id) {
              console.log(`Resolved playlist ID: ${playlist.id}`);
              setActualPlaylistId(playlist.id);
            } else {
              console.error(`Could not resolve playlist ID for code: ${playlistId}`);
              setError(`Could not find playlist with code: ${playlistId}`);
              setIsLoading(false);
            }
          }
        } catch (err) {
          console.error(`Error resolving playlist ID: ${err}`);
          setError('Error resolving playlist ID');
          setIsLoading(false);
        }
      } else {
        // It's already a UUID
        setActualPlaylistId(playlistId);
      }
    };
    
    resolvePlaylistId();
  }, [playlistId]);

  // Then fetch derivatives once we have the actual UUID
  useEffect(() => {
    if (!actualPlaylistId) {
      return;
    }

    const fetchDerivatives = async () => {
      try {
        setIsLoading(true);
        setError(null);
        console.log(`usePlaylistDerivatives: Fetching derivatives for resolved playlist ID: ${actualPlaylistId} with limit: ${limit}`);
        
        // Use lower limit to prevent performance issues
        const safeLimit = Math.min(limit, 100);
        const result = await derivativesApi.getDerivativesForPlaylist(actualPlaylistId, { limit: safeLimit });
        
        if (result.error) {
          console.error('Error fetching derivatives:', result.error);
          setError("Error loading derivatives");
          setIsLoading(false);
          setDerivatives([]);
          return;
        }
        
        if (!result.data || result.data.length === 0) {
          console.log("usePlaylistDerivatives: No derivatives found");
          setDerivatives([]);
          setHasMore(false);
          setIsLoading(false);
          return;
        }
        
        console.log(`usePlaylistDerivatives: Successfully loaded ${result.data.length} derivatives. Total count: ${result.totalCount || 'unknown'}`);
        setDerivatives(result.data);
        setCursor(result.cursor);
        setHasMore(result.hasMore);
        setUsedMockData(false);
          
      } catch (error) {
        console.error("Error in usePlaylistDerivatives:", error);
        setError("Unable to load creations");
        setDerivatives([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDerivatives();
  }, [actualPlaylistId, limit]);

  const loadMore = async () => {
    if (!actualPlaylistId || !hasMore || !cursor) return;
    
    try {
      setIsLoading(true);
      const result = await derivativesApi.getDerivativesForPlaylist(actualPlaylistId, { 
        limit, 
        cursor 
      });
      
      if (result.data && result.data.length > 0) {
        console.log(`usePlaylistDerivatives: Loaded ${result.data.length} more derivatives`);
        setDerivatives(prev => [...prev, ...result.data]);
        setCursor(result.cursor);
        setHasMore(result.hasMore);
      } else {
        console.log(`usePlaylistDerivatives: No more derivatives to load`);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more derivatives:", error);
      setError("Unable to load more creations");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    derivatives,
    isLoading,
    error,
    usedMockData,
    hasMore,
    loadMore,
    cursor
  };
};
